import { Component } from "react"

/* Components */
import Pagination from "./Pagination"
import SaveStar from "./SaveStar"

/* Icons */
import { ChevronRightIcon, WarningIcon } from "./Icons"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../translates/translate"

/* Helpers */
import { utils } from "../helpers"


/* HOW MANY ROUNDS EVERY PAGE HOLDS — THE LISTS OF THE LIVE GAMES ASK THE SERVER FOR TEN */
const SIZE = 10


/*
    Component GameList — ONE ROUND LIST FOR EVERY GAME

    columns   what stands between the number of the round and its date, see the types below
    data      the rounds of the open page
    page      the open page, the index of a row is counted from it
    size      the page size the server was asked for
    count     how many rounds the whole filter holds
    postfix   the game of a saved round, without it no star is drawn
    onOpen    a click on a row
    onPage    another page was asked for

    A column is one of:
      { key, text, type: "status" }            the paused and the completed state of the round
      { key, text, type: "money" }             credit, debit and the net of the round
      { key, text, value: (item) => "..." }    a plain value
      { key, text, render: (item) => <div />}  anything of its own
*/
class GameList extends Component {

    /* THE FAILED AND THE HANGING TRANSACTIONS OF A ROUND */
    getProblem = (item) => {

        /* Fields */
        const failed = parseInt(item.failed) > 0 ? parseInt(item.failed) : 0
        const pending = parseInt(item.pending) > 0 ? parseInt(item.pending) : 0

        /* Check Counters */
        if (failed === 0 && pending === 0) {
            return null
        }

        /* A Failure Outweighs A Hanging Request */
        return {
            tone: failed > 0 ? "failed" : "pending",
            count: failed + pending,
            hint: [
                failed > 0 ? `${t("gtx.failed")}: ${failed}` : "",
                pending > 0 ? `${t("gtx.pending")}: ${pending}` : "",
            ].filter((part) => part).join(" · "),
        }
    }

    /* Draw The State Of A Round */
    _status = (item) => (
        <div className="gl--badges">

            {parseInt(item.isPaused) === 1 && <div className="gl--badge red">{t("widget.paused")}</div>}

            {parseInt(item.status) === 1
                ? <div className="gl--badge blue">{t("widget.completed")}</div>
                : <div className="gl--badge violet">{t("widget.notCompleted")}</div>
            }

        </div>
    )

    /* Draw The Money Of A Round */
    _money = (item) => {

        /* Fields */
        const ggr = parseFloat(item.ggr)
        const tone = ggr > 0 ? "green" : (ggr < 0 ? "red" : "")

        return (
            <div className="gl--money">

                <div className="gl--money-row">
                    <span>CREDIT</span>
                    <p>{utils.convertor(item.credit, "$")}</p>
                </div>

                <div className="gl--money-row">
                    <span>DEBIT</span>
                    <p>{utils.convertor(item.debit, "$")}</p>
                </div>

                <div className={`gl--money-net ${tone}`}>
                    {ggr > 0 ? "+ " : (ggr < 0 ? "- " : "")}
                    {utils.convertor(Math.abs(ggr), "$")}
                </div>

            </div>
        )
    }

    /* Draw The Time Of A Round */
    _time = (item) => {

        /* Fields */
        const datetime = utils.getTime(item.createdAt)

        return (
            <div className="gl--time">
                <p>{datetime.time}</p>
                <span>{datetime.date}</span>
            </div>
        )
    }

    /* Draw One Cell Of A Column */
    _cell = (column, item) => {

        /* Its Own Node */
        if (column.render) {
            return column.render(item)
        }

        /* The State Of The Round */
        if (column.type === "status") {
            return this._status(item)
        }

        /* The Money Of The Round */
        if (column.type === "money") {
            return this._money(item)
        }

        /* A Plain Value */
        const value = column.value ? column.value(item) : item[column.key]

        return <div className="gl--text">{`${utils.checkValue(value)}`.trim() === "" ? "—" : value}</div>
    }

    /* Draw The Number Of A Round Together With Its Trouble */
    _number = (item) => {

        /* Fields */
        const problem = this.getProblem(item)

        return (
            <div className="gl--number">

                <div className="gl--round">#{item.number}</div>

                {/* The money of the round never went through — the operator has to look inside */}
                {problem &&
                    <div className={`gl--problem ${problem.tone}`} title={problem.hint}>
                        <WarningIcon size={12} />
                        {problem.count}
                    </div>
                }

            </div>
        )
    }

    /* Draw One Round */
    _row = (item, index) => {

        /* Fields */
        const { columns, page, size, postfix, onOpen } = this.props

        /* Fields */
        const problem = this.getProblem(item)
        const position = ((page > 0 ? page : 1) - 1) * (size ? size : SIZE) + index + 1

        return (
            <div onClick={() => onOpen(item)} className={`gl--row ${problem ? `problem ${problem.tone}` : ""}`} key={`${item.id}`}>

                <div className="gl--cell index">{position}</div>

                <div className="gl--cell" data-label={t("widget.gameNumber")}>{this._number(item)}</div>

                {columns.map((column) =>
                    <div className="gl--cell" style={{ flex: column.flex ? column.flex : 1 }} data-label={column.text} key={`${column.key}`}>
                        {this._cell(column, item)}
                    </div>
                )}

                <div className="gl--cell last" data-label={t("widget.date")}>

                    {this._time(item)}

                    {/* A round is saved by the game it belongs to */}
                    {postfix && <SaveStar gameID={item.id} postfix={postfix} saved={item.saved} />}

                    <div className="gl--arrow"><ChevronRightIcon size={14} /></div>

                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { columns, data, page, size, count, onPage } = this.props

        return (
            <div className="gl">

                <div className="gl--head">

                    <div className="gl--cell index">#</div>

                    <div className="gl--cell">{t("widget.gameNumber")}</div>

                    {columns.map((column) =>
                        <div className="gl--cell" style={{ flex: column.flex ? column.flex : 1 }} key={`${column.key}`}>{column.text}</div>
                    )}

                    <div className="gl--cell last">{t("widget.date")}</div>

                </div>

                {/* Rounds */}
                {data.map((item, index) => this._row(item, index))}

                {/* Empty List */}
                {data.length === 0 && <div className="gl--empty">{t("layout.empty")}</div>}

                <Pagination size={size ? size : SIZE} page={page} total={count} onPageChange={(value) => onPage(value)} />

                {/* The legend of the marker — it stands under the list, not over every row */}
                {data.some((item) => this.getProblem(item)) &&
                    <div className="gl--legend">
                        <WarningIcon size={13} />
                        <span>{t("widget.problemHint")}</span>
                    </div>
                }

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(GameList)
